import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icons } from "../SkillsAcademy/icons.jsx";
import ActionButton from "../ActionButton/ActionButton";
import type { Enrollment } from "../../contexts/FreeTrialContext";
import UnlockModal from "../UnlockModal/UnlockModal";
import {
  MyCardRoot,
  MyCardPosterWrap,
  MyCardPosterImg,
  MyCardPosterOverlay,
  MyCardTrialBadge,
  MyCardActiveBadge,
  MyCardCompletedPosterBadge,
  MyCardCategoryOverlay,
  MyCardScorePill,
  MyCardBody,
  MyCardDaysPill,
  MyCardStreakPill,
  MyCardTitle,
  MyCardGuruRow,
  MyCardGuruAvatar,
  MyCardGuruName,
  MyCardStatsRow,
  MyCardStatChip,
  MyProgressBarTrack,
  MyProgressBarFill,
  MyProgressStats,
  MyNextUpSection,
  MyNextUpLabel,
  MyNextUpContent,
  MyNextUpIconBox,
  MyNextUpText,
  MyNextUpTitle,
  MyCardActions,
} from "./style.js";

// ── Helpers ───────────────────────────────────────────────────────────────────

const formatLastActivity = (iso: string): string => {
  const date = new Date(iso);
  const now = new Date();
  const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return `Today, ${date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`;
  } else if (diffDays === 1) {
    return "Yesterday";
  } else if (diffDays < 7) {
    return `${diffDays} days ago`;
  }
  return date.toLocaleDateString([], { day: "numeric", month: "short" });
};

// ── Types ─────────────────────────────────────────────────────────────────────

interface ProgramData {
  id: string;
  title: string;
  category?: string;
  poster?: string;
  guru?: { name: string; avatar: string };
  [key: string]: unknown;
}

interface Props {
  enrollment: Enrollment;
}

// ── Component ─────────────────────────────────────────────────────────────────

const ProgramCard = ({ enrollment }: Props) => {
  const [hovered, setHovered] = useState(false);
  const [unlockOpen, setUnlockOpen] = useState(false);
  const navigate = useNavigate();

  const program = enrollment.program as ProgramData;
  const isTrial = enrollment.type === "trial";
  const isCompleted = enrollment.completed ?? false;

  const openDetail = () => navigate(`/dashboard/trainingprogram/${program.id}`);

  return (
    <>
    <MyCardRoot
      $isTrial={isTrial}
      $hovered={hovered}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={openDetail}
    >
      {/* ── Poster ── */}
      <MyCardPosterWrap>
        <MyCardPosterImg $src={program.poster} />
        <MyCardPosterOverlay />

        {/* Type badge — top-left */}
        {isTrial ? (
          <MyCardTrialBadge>
            <Icons.zap size={10} /> Free Trial
          </MyCardTrialBadge>
        ) : isCompleted ? (
          <MyCardCompletedPosterBadge>
            <Icons.award size={11} /> Completed
          </MyCardCompletedPosterBadge>
        ) : (
          <MyCardActiveBadge>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#17B26A", flexShrink: 0 }} />
            Active
          </MyCardActiveBadge>
        )}

        {/* Category — bottom-left */}
        {program.category && (
          <MyCardCategoryOverlay>{program.category}</MyCardCategoryOverlay>
        )}
      </MyCardPosterWrap>

      {/* ── Body ── */}
      <MyCardBody>
        {/* Status pill */}
        {isTrial ? (
          <MyCardDaysPill>
            <Icons.clock size={12} />
            {enrollment.daysLeftInTrial ?? 10} days left
          </MyCardDaysPill>
        ) : !isCompleted && enrollment.streak ? (
          <MyCardStreakPill>
            <Icons.flame size={12} />
            {enrollment.streak}-day streak
          </MyCardStreakPill>
        ) : null}

        {/* Title */}
        <MyCardTitle $isTrial={isTrial}>{program.title}</MyCardTitle>

        {/* Guru */}
        {program.guru && (
          <MyCardGuruRow>
            <MyCardGuruAvatar $src={program.guru.avatar} />
            <MyCardGuruName>with {program.guru.name}</MyCardGuruName>
          </MyCardGuruRow>
        )}

        {/* Score pill — completed programs only */}
        {isCompleted && enrollment.earnedScore != null && (
          <MyCardScorePill>
            ★ {enrollment.earnedScore.toFixed(1)} /10
          </MyCardScorePill>
        )}

        {/* Sessions + sims stat row */}
        <MyCardStatsRow>
          <MyCardStatChip>
            <Icons.book size={13} style={{ color: "#A87EFF" }} />
            {enrollment.sessionsCompleted} / {enrollment.sessionsTotal} sessions
          </MyCardStatChip>
          <MyCardStatChip>
            <Icons.mic size={13} style={{ color: "#A87EFF" }} />
            {enrollment.simulationsCompleted} / {enrollment.simulationsTotal} simulations
          </MyCardStatChip>
        </MyCardStatsRow>

        {/* Progress bar */}
        <MyProgressBarTrack>
          <MyProgressBarFill $pct={enrollment.overallProgress} $isCompleted={isCompleted} />
        </MyProgressBarTrack>
        <MyProgressStats>
          <span><strong>{enrollment.overallProgress}%</strong> complete</span>
          {isCompleted && enrollment.completedOn ? (
            <span>Completed {formatLastActivity(enrollment.completedOn)}</span>
          ) : !isCompleted ? (
            <span>Last activity {formatLastActivity(enrollment.lastActivity)}</span>
          ) : null}
        </MyProgressStats>

        {/* Up next */}
        {!isCompleted && enrollment.nextSession && (() => {
          const isSim = enrollment.nextSession.type === "sim";
          return (
            <MyNextUpSection>
              <MyNextUpLabel>Up next</MyNextUpLabel>
              <MyNextUpContent>
                <MyNextUpIconBox $isSim={isSim}>
                  {isSim ? (
                    <Icons.mic size={13} style={{ color: "#A87EFF" }} />
                  ) : (
                    <Icons.book size={13} style={{ color: "#01ECFF" }} />
                  )}
                </MyNextUpIconBox>
                <MyNextUpText>
                  <MyNextUpTitle>{enrollment.nextSession.title}</MyNextUpTitle>
                </MyNextUpText>
              </MyNextUpContent>
            </MyNextUpSection>
          );
        })()}
      </MyCardBody>

      {/* ── Actions ── */}
      <MyCardActions onClick={(e) => e.stopPropagation()}>
        {isCompleted ? (
          <>
            <ActionButton
              variant="primary"
              size="sm"
              fullWidth
              icon={<Icons.award size={14} />}
              type="button"
              onClick={openDetail}
            >
              View achievement
            </ActionButton>
            <ActionButton
              variant="secondary"
              size="sm"
              type="button"
              onClick={openDetail}
            >
              Replay
            </ActionButton>
          </>
        ) : isTrial ? (
          <>
            <ActionButton
              variant="primary"
              size="sm"
              fullWidth
              iconRight={<Icons.arrowRight size={14} />}
              type="button"
              onClick={openDetail}
            >
              Continue trial
            </ActionButton>
            <ActionButton
              variant="secondary"
              size="sm"
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setUnlockOpen(true);
              }}
            >
              Unlock full
            </ActionButton>
          </>
        ) : (
          <>
            <ActionButton
              variant="primary"
              size="sm"
              fullWidth
              iconRight={<Icons.arrowRight size={14} />}
              type="button"
              onClick={openDetail}
            >
              Continue learning
            </ActionButton>
            <ActionButton
              variant="secondary"
              size="sm"
              type="button"
              onClick={openDetail}
            >
              View
            </ActionButton>
          </>
        )}
      </MyCardActions>
    </MyCardRoot>
    <UnlockModal open={unlockOpen} onClose={() => setUnlockOpen(false)} programId={program.id} />
    </>
  );
};

export default ProgramCard;
